import axios from 'axios';

// Automatically detect API URL based on environment
// Priority: Runtime hostname detection > .env file
const getApiBaseURL = (): string => {
  // Runtime detection based on current browser location (highest priority)
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const currentPort = window.location.port;
  
  console.log('[API Config] ========================================');
  console.log('[API Config] Runtime Detection:');
  console.log('[API Config]   - hostname:', hostname);
  console.log('[API Config]   - protocol:', protocol);
  console.log('[API Config]   - port:', currentPort);
  console.log('[API Config] Environment Variables:');
  console.log('[API Config]   - NODE_ENV:', process.env.NODE_ENV);
  console.log('[API Config]   - REACT_APP_ENV:', process.env.REACT_APP_ENV);
  console.log('[API Config]   - REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  
  // LOCAL DEVELOPMENT - Check FIRST (runtime detection overrides .env)
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '';
  const isLocalNetwork = hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.');
  const isDevServer = protocol === 'http:' && (currentPort === '3000' || currentPort === '3001' || currentPort === '');
  const isNotProductionDomain = !hostname.includes('geekspromax.com') && !hostname.includes('creditcardoptimizer');
  
  // If accessing via localhost or local network, ALWAYS use local API (ignore .env)
  if (isLocalhost || (isLocalNetwork && isNotProductionDomain) || (isDevServer && isNotProductionDomain)) {
    const localUrl = 'http://localhost:8000/api';
    console.log('[API Config] ✅ LOCAL DEVELOPMENT DETECTED');
    console.log('[API Config] ✅ Using local API:', localUrl);
    console.log('[API Config] ========================================');
    return localUrl;
  }
  
  // PRODUCTION - Check for production domain
  if (hostname.includes('geekspromax.com') && protocol === 'https:') {
    // Use .env variable if set, otherwise use default production URL
    const prodUrl = process.env.REACT_APP_API_URL || 'https://creditcardoptimizer-api.geekspromax.com/api';
    console.log('[API Config] ✅ PRODUCTION DETECTED');
    console.log('[API Config] ✅ Using production API:', prodUrl);
    console.log('[API Config] ========================================');
    return prodUrl;
  }
  
  // FALLBACK - Use .env variable if set (for custom deployments)
  if (process.env.REACT_APP_API_URL) {
    console.log('[API Config] ⚠️ Using .env REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('[API Config] ========================================');
    return process.env.REACT_APP_API_URL;
  }
  
  // DEFAULT FALLBACK - Construct from current location
  const fallbackUrl = `${protocol}//${hostname}${currentPort ? ':' + currentPort : ''}/api`;
  console.log('[API Config] ⚠️ Using fallback URL:', fallbackUrl);
  console.log('[API Config] ========================================');
  return fallbackUrl;
};

// Get base URL once at module load
const API_BASE_URL = getApiBaseURL();
console.log('[API Config] 🚀 Final API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Increase timeout for file uploads
  timeout: 300000, // 5 minutes for large PDF files
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh/`,
            { refresh: refreshToken }
          );
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (err) {
          // Token refresh failed - clear all auth data
          console.warn('Token refresh failed, logging out user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Dispatch custom event to notify App.tsx to update auth state
          window.dispatchEvent(new CustomEvent('auth:logout'));
          // Only redirect if not already on login/signup page
          if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
            window.location.href = '/login';
          }
        }
      } else {
        // No refresh token - clear auth data and redirect
        console.warn('No refresh token available, logging out user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Dispatch custom event to notify App.tsx to update auth state
        window.dispatchEvent(new CustomEvent('auth:logout'));
        if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
